/**
 * Webinar (fixed-time group session) helpers for the scheduling system.
 * Pure, browserless logic — shared by the booking page, the event-type
 * dialog and the host view. Covered by Vitest (webinar.test.ts).
 */

export interface EventSession {
    id: string;
    event_type_id?: string;
    start_time: string | Date;
    end_time: string | Date;
    timezone?: string | null;
    capacity?: number | null;
    status?: string | null;
    booked_count?: number;
    remaining_spots?: number | null;
    is_full?: boolean;
    meeting_url?: string | null;
}

export interface BookingPrefill {
    name: string;
    email: string;
    phone: string;
}

/** An event type registers invitees into fixed sessions instead of slots. */
export const isWebinarEventType = (eventType: any): boolean =>
    eventType?.event_kind === 'webinar';

/** Upcoming, non-cancelled sessions sorted soonest-first. */
export const upcomingSessions = (
    sessions: EventSession[] | null | undefined,
    now: Date = new Date()
): EventSession[] =>
    (Array.isArray(sessions) ? sessions : [])
        .filter(s => {
            if (!s || s.status === 'cancelled') return false;
            const end = new Date(s.end_time);
            return !isNaN(end.getTime()) && end.getTime() > now.getTime();
        })
        .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());

/** Remaining seats for a session; null = unlimited. */
export const sessionRemainingSpots = (session: EventSession): number | null => {
    if (session.capacity === null || session.capacity === undefined) return null;
    if (typeof session.remaining_spots === 'number') return Math.max(0, session.remaining_spots);
    const booked = typeof session.booked_count === 'number' ? session.booked_count : 0;
    return Math.max(0, Number(session.capacity) - booked);
};

/** True when the session cannot accept more registrations. */
export const isSessionFull = (session: EventSession): boolean => {
    const remaining = sessionRemainingSpots(session);
    return remaining !== null && remaining <= 0;
};

/** "5 slots left" / "Full" / null (unlimited — show nothing). */
export const sessionSpotsLabel = (session: EventSession): string | null => {
    const remaining = sessionRemainingSpots(session);
    if (remaining === null) return null;
    if (remaining <= 0) return 'Full';
    return `${remaining} slot${remaining === 1 ? '' : 's'} left`;
};

/**
 * Parse invitee prefill from a booking-link query string
 * (?name=…&email=…&phone=…). Also accepts the invitee_* aliases so links
 * generated from campaign variables work either way. Values are trimmed and
 * length-capped; a malformed search string never throws.
 */
export const parseBookingPrefill = (search: string): BookingPrefill => {
    const empty: BookingPrefill = { name: '', email: '', phone: '' };
    try {
        const params = new URLSearchParams(search || '');
        const pick = (...keys: string[]): string => {
            for (const key of keys) {
                const value = (params.get(key) || '').trim();
                if (value) return value.slice(0, 255);
            }
            return '';
        };
        return {
            name: pick('name', 'invitee_name'),
            email: pick('email', 'invitee_email'),
            phone: pick('phone', 'invitee_phone'),
        };
    } catch {
        return empty;
    }
};

/**
 * Split a prefilled phone into a country code + local digits for the
 * code-select + number-input pair. Longest matching code wins; no match
 * returns code null (caller keeps its default) with the digits preserved.
 */
export const splitPhonePrefill = (
    phone: string,
    codes: string[]
): { code: string | null; number: string } => {
    const raw = (phone || '').trim();
    if (!raw) return { code: null, number: '' };
    const withPlus = raw.startsWith('+') ? raw : `+${raw}`;
    const sorted = [...codes].sort((a, b) => b.length - a.length);
    for (const code of sorted) {
        if (withPlus.startsWith(code) && withPlus.length > code.length) {
            return { code, number: withPlus.slice(code.length).replace(/[^\d]/g, '') };
        }
    }
    return { code: null, number: raw.replace(/[^\d]/g, '') };
};

// ── Paid registration ───────────────────────────────────────────────────────

/**
 * Human price label for a paid event. null = free (render nothing / "Free").
 * DECIMAL columns arrive as strings from the API — coerce defensively.
 */
export const formatEventPrice = (
    price: unknown,
    currency?: string | null
): string | null => {
    const num = Number(price);
    if (!Number.isFinite(num) || num <= 0) return null;
    const cur = (currency || 'INR').toUpperCase();
    const rendered = Number.isInteger(num) ? String(num) : num.toFixed(2);
    return cur === 'INR' ? `₹${rendered}` : `${cur} ${rendered}`;
};

/** An event type collects payment before confirming a registration. */
export const isPaidEventType = (eventType: any): boolean =>
    formatEventPrice(eventType?.price, eventType?.currency) !== null;

// ── Paid registration → checkout redirect ──────────────────────────────────

/** Minimal shape of `window` this helper touches, so tests can pass a fake. */
export interface CheckoutWindowLike {
    location: { href: string };
    top?: CheckoutWindowLike | null;
}

/**
 * Send the browser to a payment checkout page, breaking OUT of the
 * scheduler-widget iframe first.
 *
 * Why top-level and not `window.location`: the public booking page is embedded
 * on customers' own websites by
 * [scheduler-widget.js](../../../public/scheduler-widget.js). Navigating with
 * `window.location` keeps Razorpay — and then our `/booking-success/:id` return
 * page — inside that cross-origin frame. Since Aug 2026 the app is served with
 * `X-Frame-Options: SAMEORIGIN` (added for the Zoom Marketplace security
 * review), so the return page would be blocked in that frame. Navigating the top
 * window avoids that, and is the correct behaviour for a checkout page anyway —
 * the customer can see the real payment URL in the address bar.
 *
 * `win.top.location.href` is only ever WRITTEN, never read: reading a
 * cross-origin `location` throws, whereas navigating the top-level context is
 * permitted when the action follows a user gesture (the Register & Pay click).
 *
 * Fails soft: if the browser refuses the top-level navigation (e.g. no user
 * activation), we navigate inside the frame instead, so the customer can still
 * pay. The booking is confirmed by the Razorpay webhook, not by the return page.
 *
 * @returns where it navigated — `'top'`, `'self'`, or `'blocked'` if there was
 *          nothing to navigate (no url / no window, e.g. SSR).
 */
export const redirectToCheckout = (
    url: string,
    win: CheckoutWindowLike | null = typeof window === 'undefined'
        ? null
        : (window as unknown as CheckoutWindowLike)
): 'top' | 'self' | 'blocked' => {
    if (!url || !win) return 'blocked';
    try {
        const top = win.top;
        if (top && top !== win) {
            top.location.href = url;
            return 'top';
        }
    } catch {
        // Cross-origin top navigation refused — fall through to in-frame.
    }
    win.location.href = url;
    return 'self';
};

// ── Registrant CSV export (host side) ──────────────────────────────────────

export interface RegistrantCsvRow {
    invitee_name?: string | null;
    invitee_email?: string | null;
    invitee_phone?: string | null;
    session_label?: string | null;
    status?: string | null;
    /** 'paid' | 'pending' | null (null = free event, nothing to pay). */
    payment_status?: string | null;
    created_at?: string | Date | null;
}

/**
 * Display status for a single registrant row.
 *
 * Previously the host UI showed anything not cancelled as "Registered", which
 * was only safe because the backend hides `pending_payment` rows from
 * `getBookings`. That made the dialog's correctness depend on a filter three
 * layers away: relax it and abandoned checkouts would render as confirmed
 * registrants and inflate the host's seat count — exactly what the
 * pending_payment design exists to prevent. Deciding it here from the row's own
 * fields makes the UI correct on its own.
 *
 * Checks BOTH `status === 'pending_payment'` and `payment_status === 'pending'`
 * because they are written by different paths (createBooking sets the status;
 * confirmBookingPayment flips both) and either alone means "no seat yet".
 */
export const registrantStatusLabel = (
    row: Pick<RegistrantCsvRow, 'status' | 'payment_status'> | null | undefined
): 'Cancelled' | 'Awaiting payment' | 'Registered' => {
    if (row?.status === 'cancelled') return 'Cancelled';
    if (row?.status === 'pending_payment' || row?.payment_status === 'pending') {
        return 'Awaiting payment';
    }
    return 'Registered';
};

/**
 * True only when a registration actually occupies a seat — mirrors the
 * backend's `_seatHoldingStatusFilter()` (`NOT IN ('cancelled','pending_payment')`).
 * Use this for every count the host sees, so the dialog's totals agree with the
 * capacity math the server enforces.
 */
export const registrantHoldsSeat = (
    row: Pick<RegistrantCsvRow, 'status' | 'payment_status'> | null | undefined
): boolean => registrantStatusLabel(row) === 'Registered';

/** RFC-4180 escaping: quote fields containing commas, quotes or newlines. */
export const csvEscape = (value: unknown): string => {
    const s = value === null || value === undefined ? '' : String(value);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * Build the registrants CSV (header + one line per registration).
 * Pure — timestamps are rendered as ISO strings so the file is
 * timezone-unambiguous when opened in Sheets/Excel.
 */
export const buildRegistrantsCsv = (rows: RegistrantCsvRow[]): string => {
    const header = ['Name', 'Email', 'Phone', 'Session', 'Status', 'Registered At'];
    const lines = (Array.isArray(rows) ? rows : []).map(r => {
        const registered = r.created_at ? new Date(r.created_at) : null;
        return [
            csvEscape(r.invitee_name || ''),
            csvEscape(r.invitee_email || ''),
            csvEscape(r.invitee_phone || ''),
            csvEscape(r.session_label || ''),
            csvEscape(r.status || 'confirmed'),
            csvEscape(registered && !isNaN(registered.getTime()) ? registered.toISOString() : ''),
        ].join(',');
    });
    return [header.join(','), ...lines].join('\r\n');
};

/**
 * Rows for the Excel export (one object per registration, keys = column
 * headers). Pure — feed straight into XLSX.utils.json_to_sheet.
 */
export const buildRegistrantSheetRows = (
    rows: RegistrantCsvRow[]
): Record<string, string>[] =>
    (Array.isArray(rows) ? rows : []).map(r => {
        const registered = r.created_at ? new Date(r.created_at) : null;
        return {
            'Name': r.invitee_name || '',
            'Email': r.invitee_email || '',
            'Phone': r.invitee_phone || '',
            'Session': r.session_label || '',
            'Status': registrantStatusLabel(r),
            'Registered At': registered && !isNaN(registered.getTime())
                ? registered.toISOString().replace('T', ' ').slice(0, 19)
                : '',
        };
    });

/**
 * Case-insensitive registrant filter over name / email / phone.
 * Empty query returns the input untouched.
 */
export const filterRegistrants = <T extends RegistrantCsvRow>(rows: T[], query: string): T[] => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
        (r.invitee_name || '').toLowerCase().includes(q) ||
        (r.invitee_email || '').toLowerCase().includes(q) ||
        (r.invitee_phone || '').toLowerCase().includes(q)
    );
};

/**
 * Sanitize a label into a valid, unique Excel worksheet name.
 * Excel rules: max 31 chars, no : \ / ? * [ ], not empty. `taken` is the set
 * of names already used in the workbook — collisions get a " 2", " 3"… suffix.
 */
export const excelSheetName = (label: string, taken: Set<string>, fallback = 'Session'): string => {
    let base = (label || '')
        .replace(/[:\\/?*[\]]/g, '.')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 31) || fallback;
    let name = base;
    let n = 2;
    while (taken.has(name)) {
        const suffix = ` ${n}`;
        name = base.slice(0, 31 - suffix.length) + suffix;
        n += 1;
    }
    taken.add(name);
    return name;
};

/** Human label for a session used in dialogs and the CSV Session column. */
export const sessionDateLabel = (session: Pick<EventSession, 'start_time' | 'end_time'>): string => {
    const start = new Date(session.start_time);
    const end = new Date(session.end_time);
    if (isNaN(start.getTime())) return '';
    const date = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    const fmtTime = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return isNaN(end.getTime()) ? `${date} ${fmtTime(start)}` : `${date} ${fmtTime(start)} – ${fmtTime(end)}`;
};

/** ISO / Date → value for <input type="datetime-local"> in the local tz. */
export const toDatetimeLocalValue = (value: string | Date | null | undefined): string => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** <input type="datetime-local"> value → ISO string (or null if invalid). */
export const fromDatetimeLocalValue = (value: string): string | null => {
    if (!value || typeof value !== 'string') return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
};
